import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch } from "app/hooks";
import ScheduleNextAppointmentModal from "components/schedule-next-appointment-modal";
import { EVENT_TYPE_APPOINTMENT } from "constants/appointments";
import { SCHEDULE_VIEW_IN_PATIENT_HEADER } from "constants/feature-flags";
import { useSchedulerChoice } from "features/app-layout/scheduler-choice/use-scheduler-choice.hook";
// The hand-off contract only: a file with no imports of its own, so nothing here pulls the
// scheduler into the chart's chunk.
import {
  SCHEDULING_FOR_STATE_KEY,
  type SchedulingForRequest,
  schedulingForInitials
} from "features/scheduling-v3/domain/scheduling-for-request";
import FindNextAvailableDialog, {
  type FindNextAvailableInitialValues
} from "features/scheduling/find-next-available/find-next-available-dialog.component";
import { AppointmentType } from "services/types/appointment-types.type";
import { Appointment } from "services/types/appointments.type";
import { Patient } from "services/types/patients.type";
import { formatStaffMemberFullName } from "utils/common";

import {
  openForm,
  startFollowUpScheduling,
  startPatientCreateAppointmentMode
} from "../features/scheduling/scheduler-slice";
import useScheduleNextAppointmentPrompt from "./use-schedule-next-appointment-prompt";

type SetupAppointmentModalFn = (
  appointment: Partial<Appointment>,
  actionType: "add" | "update" | "delete"
) => void;

type ScheduleNextAppointmentProviderProps = {
  children?: React.ReactNode;
  setupAppointmentModal: SetupAppointmentModalFn;
  appointmentFormId: string;
};

interface ScheduleNextAppointmentContextValue {
  isModalOpen: boolean;
  completedAppointment: Appointment | null;
  initScheduleNextAppointment: (
    appointment: Appointment,
    onScheduled?: () => void
  ) => Promise<void>;
  closeModal: () => void;
  // Opens the Find Next Available search pre-seeded — used by the checkout
  // surfaces (the modal's "Find next available" action routes through the
  // provider too, and the Current Visit drawer block calls this directly).
  //
  // With Scheduling V3 on, the same seeds go to V3's search instead (ENG-2027), inside its
  // "scheduling for" mode (ENG-2032): every way out comes back to the path it left.
  openFindNextAvailable: (
    values: FindNextAvailableInitialValues,
    v3?: ChartPatient
  ) => void;
}

/**
 * `patientId`: the ROUTE's patient. The chart's entries can be clicked while its patient
 * record is still undefined (seen in a browser from the visit drawer, which is driven by
 * the active appointment), and a hand-off that depended on the record opened the OLD dialog
 * with V3 on. V3 looks a missing name up itself.
 */
interface ChartPatient {
  patientId?: number;
}

const ScheduleNextAppointmentContext =
  React.createContext<ScheduleNextAppointmentContextValue | null>(null);

export function ScheduleNextAppointmentProvider({
  children,
  setupAppointmentModal,
  appointmentFormId
}: ScheduleNextAppointmentProviderProps) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const flags = useFlags();
  const isSchedulingV3 = useSchedulerChoice().useV3;
  const isCalendarModeSupported = flags[SCHEDULE_VIEW_IN_PATIENT_HEADER];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [completedAppointment, setCompletedAppointment] =
    useState<Appointment | null>(null);
  const [onScheduledCallback, setOnScheduledCallback] = useState<
    (() => void) | undefined
  >(undefined);

  const {
    appointmentTypes,
    getPromptAppointment,
    getSuggestedAppointmentType
  } = useScheduleNextAppointmentPrompt();

  const initScheduleNextAppointment = useCallback(
    async (appointment: Appointment, onScheduled?: () => void) => {
      const promptAppointment = await getPromptAppointment(appointment);
      if (!promptAppointment) return;

      setCompletedAppointment(promptAppointment as Appointment);
      setIsModalOpen(true);
      if (onScheduled) {
        setOnScheduledCallback(() => onScheduled);
      }
    },
    [getPromptAppointment]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setCompletedAppointment(null);
    setOnScheduledCallback(undefined);
  }, []);

  const [findNextInitialValues, setFindNextInitialValues] =
    useState<FindNextAvailableInitialValues | null>(null);

  /**
   * Hand the search to Scheduling V3, or say no (false) so the caller opens the old dialog.
   * No when V3 is off, when there is nobody to book for, and when the user is working INSIDE
   * the old scheduler: this provider also serves its checkout (grid preview, View Schedule,
   * Room Traffic), and completing a visit there must not throw them into V3.
   */
  const openInSchedulingV3 = useCallback(
    (values: FindNextAvailableInitialValues, v3?: ChartPatient): boolean => {
      const patientId = values.patient?.id ?? v3?.patientId;
      const inOldScheduler = /^\/scheduling(\/|$)/.test(location.pathname);
      if (!isSchedulingV3 || !patientId || inOldScheduler) return false;
      // ENG-2032's request, plus the search to open once its mode has started. The caller
      // resolved its own clinic and provider: pass them on, derive nothing.
      const { patient } = values;
      const request: SchedulingForRequest = {
        patientId,
        patientLabel:
          patient?.fullName ||
          `${patient?.first_name ?? ""} ${patient?.last_name ?? ""}`.trim(),
        privateLabel: schedulingForInitials(patient ?? null),
        clinicLabel: values.clinic?.name ?? null,
        returnToPath: location.pathname + location.search,
        findNext: {
          clinicId: values.clinic?.id ?? null,
          providerIds: (values.providers ?? []).map((p) => Number(p.value)),
          appointmentTypeId: values.appointmentType?.id ?? null,
          startFromMonths: values.startFromMonths ?? null
        }
      };
      navigate("/scheduling-v3", {
        state: { [SCHEDULING_FOR_STATE_KEY]: request }
      });
      return true;
    },
    [isSchedulingV3, navigate, location.pathname, location.search]
  );

  const openFindNextAvailable = useCallback(
    (values: FindNextAvailableInitialValues, v3?: ChartPatient) => {
      if (!openInSchedulingV3(values, v3)) setFindNextInitialValues(values);
    },
    [openInSchedulingV3]
  );

  // The modal's "Find next available" action: carry everything the completed
  // appointment already knows — patient, clinic, provider — plus the chosen
  // interval into the search dialog.
  const handleFindNextAvailable = useCallback(
    (appointmentType: AppointmentType, startFromMonths: number | null) => {
      if (!completedAppointment) return;

      const staffMember = completedAppointment.staff_member;
      const providers =
        staffMember && typeof staffMember === "object" && staffMember.id
          ? [
              {
                label: formatStaffMemberFullName(staffMember).fullName ?? "",
                value: String(staffMember.id)
              }
            ]
          : [];

      // The visit drawer's completion flow reaches V3 too (ENG-2027).
      const values = {
        appointmentType,
        patient: completedAppointment.patient ?? null,
        clinic: completedAppointment.clinic ?? null,
        providers,
        startFromMonths
      };
      // The visit drawer fetches the completed visit through
      // `convertAppointmentResponseToModel`, which names the provider `staffMemberId`, not
      // `staff_member`: read only as above, its provider is silently dropped and V3 falls
      // back to the grid's columns (seen in a browser). For V3 ONLY: what the old dialog
      // receives with V3 off must stay exactly what it is today.
      const drawerProviderId = (
        completedAppointment as { staffMemberId?: number | null }
      ).staffMemberId;
      const forV3 =
        providers.length || !drawerProviderId
          ? values
          : {
              ...values,
              providers: [{ label: "", value: String(drawerProviderId) }]
            };
      if (!openInSchedulingV3(forV3)) setFindNextInitialValues(values);

      setIsModalOpen(false);
      setCompletedAppointment(null);
      if (onScheduledCallback) {
        onScheduledCallback();
      }
      setOnScheduledCallback(undefined);
    },
    [completedAppointment, onScheduledCallback, openInSchedulingV3]
  );

  const handleScheduleNow = useCallback(
    (appointmentTypeId: number) => {
      if (!completedAppointment) return;

      const selectedType = appointmentTypes.find(
        (type) => type.id === appointmentTypeId
      );
      const patientForScheduling = completedAppointment.patient;

      if (isCalendarModeSupported) {
        dispatch(
          startPatientCreateAppointmentMode({
            patient: patientForScheduling,
            returnToPath: location.pathname + location.search
          })
        );
        dispatch(
          startFollowUpScheduling({
            completedAppointmentId: completedAppointment.id,
            patient: patientForScheduling,
            appointmentType: selectedType || null
          })
        );
      } else {
        // Fallback when feature flag is off: open form directly (slot-click wouldn't use patient).
        dispatch(
          startFollowUpScheduling({
            completedAppointmentId: completedAppointment.id,
            patient: patientForScheduling,
            appointmentType: selectedType || null
          })
        );
        setupAppointmentModal(
          {
            type: EVENT_TYPE_APPOINTMENT,
            patient: patientForScheduling,
            clinic: completedAppointment.clinic,
            appointment_type: selectedType || null
          } as Partial<Appointment>,
          "add"
        );
        dispatch(openForm(appointmentFormId));
      }

      setIsModalOpen(false);
      setCompletedAppointment(null);

      if (onScheduledCallback) {
        onScheduledCallback();
      }
      setOnScheduledCallback(undefined);
    },
    [
      completedAppointment,
      appointmentTypes,
      dispatch,
      isCalendarModeSupported,
      location.pathname,
      location.search,
      onScheduledCallback,
      setupAppointmentModal,
      appointmentFormId
    ]
  );

  // Get the suggested appointment type from the completed appointment
  const suggestedAppointmentType: AppointmentType | null = React.useMemo(
    () => getSuggestedAppointmentType(completedAppointment),
    [completedAppointment, getSuggestedAppointmentType]
  );

  const patient: Patient | null = completedAppointment?.patient || null;

  const contextValue = useMemo(
    () => ({
      isModalOpen,
      completedAppointment,
      initScheduleNextAppointment,
      closeModal,
      openFindNextAvailable
    }),
    [
      isModalOpen,
      completedAppointment,
      initScheduleNextAppointment,
      closeModal,
      openFindNextAvailable
    ]
  );

  return (
    <ScheduleNextAppointmentContext.Provider value={contextValue}>
      {children}

      <ScheduleNextAppointmentModal
        open={isModalOpen}
        onClose={closeModal}
        onScheduleNow={handleScheduleNow}
        onFindNextAvailable={handleFindNextAvailable}
        patient={patient}
        suggestedAppointmentType={suggestedAppointmentType}
      />

      {/* Mounted on demand so every open starts from a clean search, matching
          the scheduler entry point's behavior. */}
      {findNextInitialValues && (
        <FindNextAvailableDialog
          open
          initialValues={findNextInitialValues}
          onClose={() => setFindNextInitialValues(null)}
        />
      )}
    </ScheduleNextAppointmentContext.Provider>
  );
}

export default function useScheduleNextAppointment() {
  const context = React.useContext(ScheduleNextAppointmentContext);
  if (!context) {
    throw new Error(
      `useScheduleNextAppointment must be used within a ScheduleNextAppointmentProvider`
    );
  }

  return context;
}
